import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Shield } from "lucide-react";

export const FinalCTASection = () => {
  return (
    <section className="relative py-20 overflow-hidden">
      {/* Image Background */}
      <img 
        src="https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=1920&q=80"
        alt="Camp cooking background"
        className="absolute inset-0 w-full h-full object-cover"
      />
      
      {/* Brand gradient overlay - maintains colors + text readability */}
      <div className="absolute inset-0 bg-gradient-to-br from-midnight-green/85 via-deep-teal/80 to-midnight-green/85" />
      
      {/* Copper accent gradient - 15% opacity for premium feel */}
      <div className="absolute inset-0 bg-gradient-to-tr from-copper/15 via-transparent to-transparent" />
      
      {/* Radial center glow - spotlight effect */}
      <div 
        className="absolute inset-0" 
        style={{
          background: 'radial-gradient(circle at 50% 50%, hsl(174 63% 19% / 0.3) 0%, transparent 70%)'
        }} 
      />

      {/* Dot pattern overlay */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }} />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          {/* Headline */}
          <h2 className="font-playfair text-4xl md:text-6xl font-bold text-ivory mb-6 leading-tight">
            From Wilderness to Dining Hall —<br />
            <span className="text-copper">Let CampChef Pro Be Your Kitchen's North Star</span>
          </h2>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 my-12 max-w-3xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-copper/20">
              <div className="font-playfair text-4xl font-bold text-copper mb-2">5 min</div>
              <div className="font-inter text-ivory/80">Setup time</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-copper/20">
              <div className="font-playfair text-4xl font-bold text-copper mb-2">99.7%</div>
              <div className="font-inter text-ivory/80">Uptime reliability</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-copper/20">
              <div className="font-playfair text-4xl font-bold text-copper mb-2">40-80 hrs</div>
              <div className="font-inter text-ivory/80">Monthly time savings</div>
            </div>
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button 
              size="lg" 
              asChild
              className="bg-copper hover:bg-copper/90 text-ivory font-semibold text-lg px-8 py-6 h-auto shadow-xl"
            >
              <Link to="/contact">Request a Camp Demo</Link>
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              asChild
              className="border-2 border-ivory/30 bg-white/10 backdrop-blur-sm text-ivory hover:bg-white/20 font-semibold text-lg px-8 py-6 h-auto"
            >
              <Link to="/contact">Contact Sales</Link>
            </Button>
          </div>

          {/* Trust note */}
          <div className="flex items-center justify-center gap-2 text-ivory/70 text-sm">
            <Shield className="h-4 w-4" />
            <span className="font-inter">Your data syncs securely. Works offline when remote.</span>
          </div>
        </div>
      </div>

      {/* Animated decorative blobs */}
      <div className="absolute top-10 left-10 w-64 h-64 bg-copper/20 rounded-full blur-3xl animate-pulse-slow" />
      <div className="absolute bottom-10 right-10 w-80 h-80 bg-deep-teal/25 rounded-full blur-3xl animate-pulse-slow" 
           style={{ animationDelay: '2s' } as React.CSSProperties} />
    </section>
  );
};
